import type { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { groupBy, orderBy } from 'lodash-es'
import { useMemo } from 'react'
import { useParticipants } from '#src/hooks/useParticipants.ts'

// Define the possible task states
const ORDERED_TASK_STATES = [
  'in-progress',
  'todo',
  'backlog',
  'done',
  'canceled',
  'duplicate',
] as const

// TODO: This should be TaskViewGrouping from seen/core, but that leads to a type error if used.
export type TaskListGrouping = 'state' | 'participant'

export type TaskListSortField =
  | 'priority'
  | 'authoredOn'
  | 'dueOn'
  | 'lastUpdated'
  | 'title'

export interface TaskListSort {
  field: TaskListSortField
  direction: 'asc' | 'desc'
}

export type TaskListItem =
  | {
      key: string
      type: 'state-group'
      state: StandardTask['state']
      tasks: StandardTask[]
    }
  | {
      key: string
      type: 'participant-group'
      participant: string | null
      tasks: StandardTask[]
    }
  | {
      key: string
      type: 'task'
      task: StandardTask
    }

export function useTaskListItems(
  tasks: StandardTask[] | undefined,
  {
    grouping,
    sort,
  }: {
    grouping?: TaskListGrouping
    sort?: TaskListSort
  } = {}
): TaskListItem[] {
  const participants = useParticipants()
  const taskRows = useMemo(() => {
    const effectiveGrouping = grouping ?? 'state'
    if (effectiveGrouping === 'state') {
      const groupedTasks = groupBy(tasks, (task) => task.state) as Partial<
        Record<StandardTask['state'], StandardTask[]>
      >
      return ORDERED_TASK_STATES.flatMap((state) => {
        const stateTasks = groupedTasks[state]
        if (!stateTasks?.length) {
          return []
        }

        const sortedTasks = sortTasks(stateTasks, sort)

        return [
          {
            type: 'state-group' as const,
            key: state,
            state,
            tasks: sortedTasks,
          },
          ...sortedTasks.map((task) => ({
            type: 'task' as const,
            key: task.id,
            task,
          })),
        ]
      })
    }
    if (effectiveGrouping === 'participant') {
      const tasksByParticipant = groupBy(
        tasks,
        (task) => task.participant?.reference ?? NO_PARTICIPANT_KEY
      ) as Partial<Record<string, StandardTask[]>>
      return [...participants, null].flatMap((participant) => {
        const taskGroup =
          tasksByParticipant[
            participant?.reference.reference ?? NO_PARTICIPANT_KEY
          ]
        if (!taskGroup?.length) {
          return []
        }

        const sortedTasks = sort
          ? sortTasks(taskGroup, sort)
          : orderBy(
              taskGroup,
              [
                (task) => task.stateOrder,
                (task) => task.priorityNumber ?? 9999, // Tasks without priority go last
                (task) => task.dueOn ?? '9999-12-31', // Tasks without due dates go last
              ],
              ['asc', 'asc', 'asc']
            )

        return [
          {
            type: 'participant-group' as const,
            key: participant?.reference.reference ?? NO_PARTICIPANT_KEY,
            participant: participant?.reference.reference ?? null,
            tasks: sortedTasks,
          },
          ...sortedTasks.map((task) => ({
            type: 'task' as const,
            key: task.id,
            task,
          })),
        ]
      })
    }
    throw new Error(`Unknown grouping: ${effectiveGrouping}`)
  }, [tasks, grouping, sort, participants])

  return taskRows
}

export function sortTasks(
  tasks: StandardTask[],
  sort: TaskListSort | undefined
): StandardTask[] {
  const direction = sort?.direction ?? 'asc'
  if (!sort || sort.field === 'priority') {
    // Sort by priority then due date. Direction-aware sentinels keep tasks
    // missing priority (or a due date) at the bottom in both ascending and
    // descending modes.
    const priorityMissing = direction === 'asc' ? 9999 : -1
    const dueMissing = direction === 'asc' ? '9999-12-31' : '0000-01-01'
    return orderBy(
      tasks,
      [
        (task) => task.priorityNumber ?? priorityMissing,
        (task) => task.dueOn ?? dueMissing,
      ],
      [direction, direction]
    )
  }
  // Tasks missing the sort field go last regardless of direction.
  const missingSentinel = direction === 'asc' ? '~' : ''
  return orderBy(
    tasks,
    [(task) => sortKey(task, sort.field, missingSentinel)],
    [direction]
  )
}

function sortKey(
  task: StandardTask,
  field: TaskListSortField,
  missingSentinel: string
): string | number {
  switch (field) {
    case 'authoredOn':
      return safeAuthoredOn(task) ?? missingSentinel
    case 'dueOn':
      return task.dueOn ?? missingSentinel
    case 'lastUpdated':
      return safeLastUpdated(task) ?? missingSentinel
    case 'title':
      return (task.title ?? '').toLowerCase() || missingSentinel
    default:
      return missingSentinel
  }
}

function safeAuthoredOn(task: StandardTask): string | undefined {
  // `task.authoredOn` invariants on missing values; guard so sorting never throws.
  try {
    return task.authoredOn
  } catch {
    return undefined
  }
}

function safeLastUpdated(task: StandardTask): string | undefined {
  // `task.lastUpdated` throws on unpersisted resources; guard so sorting never throws.
  try {
    return task.lastUpdated
  } catch {
    return undefined
  }
}

const NO_PARTICIPANT_KEY = 'no-participant'
